/**
 * Order → Stages tab (mockup docs/design/marketplace-hire/SignOff.dc.html):
 * every stage with its status, date and amount, then the panel for the stage
 * being worked on — what THIS person can do now, shown only from `order.can`.
 */
import { useEffect, useMemo, useState } from 'react'
import { fmtDate, money } from '@/api/marketplace'
import {
  cancelOrder, disputeStage, payStage, requestStageChanges, approveStage, submitStage,
  type Order, type OrderStage,
} from '@/api/marketplaceBuild'
import {
  NoteField, StagePill, currentStage, firstName, fmtDateTime, isLast, splitDeliverables, type OrderCtx,
} from './orderShared'
import { FinalSignOff, InstallPanel } from './orderSignOff'

export default function OrderStagesTab(ctx: OrderCtx) {
  const { order } = ctx
  const cur = currentStage(order)
  const open = order.status === 'in_progress'
  return (
    <div className="mkt-col" style={{ gap: 20 }}>
      <InstallPanel {...ctx} />
      {open && cur && <CurrentStagePanel ctx={ctx} stage={cur} />}
      {open && !cur && !order.can.install && (
        <div className="mkt-banner plain"><div><strong>Every stage is done</strong>Twasta is finishing the last payment and the install.</div></div>
      )}
      <StageTable order={order} />
      {open && cur && ['in_review', 'changes_requested', 'approved', 'payment_due'].includes(cur.status) && (
        <DisputePanel ctx={ctx} stage={cur} />
      )}
      {order.can.cancel && <CancelPanel ctx={ctx} />}
    </div>
  )
}

function StageTable({ order }: { order: Order }) {
  const cur = order.currency
  const client = order.role === 'client'
  return (
    <section className="mkt-panel mkt-col" aria-label="All stages">
      <h2>Stages</h2>
      <div className="mkt-scroll">
        <table className="mkt-table">
          <thead>
            <tr>
              <th scope="col">#</th><th scope="col">Stage</th><th scope="col">Target date</th>
              <th scope="col" className="num">Amount</th><th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {order.stages.map((s, i) => (
              <tr key={s.idx} style={s.idx === order.current_idx ? { background: 'var(--color-accent-weak)' } : undefined}
                aria-current={s.idx === order.current_idx ? 'step' : undefined}>
                <td>{i === 0 ? '·' : i}</td>
                <td>
                  <strong>{s.name}</strong> <span className="mkt-faint">· {s.pct}%</span>
                  {s.approves && <div className="mkt-faint">{s.approves}</div>}
                </td>
                <td>{s.target_date ? fmtDate(s.target_date) : '—'}</td>
                <td className="num">
                  {money(s.amount_minor, cur)}
                  {client && s.client.service_fee_minor > 0 && <div className="mkt-faint">+ {money(s.client.service_fee_minor, cur)} service fee</div>}
                </td>
                <td>
                  <StagePill stage={s} role={order.role} />
                  {s.released_at && <div className="mkt-faint">{fmtDate(s.released_at)}</div>}
                  {!s.released_at && s.approved_at && <div className="mkt-faint">Approved {fmtDate(s.approved_at)}{s.auto_approved ? ' (automatically)' : ''}</div>}
                  {s.on_hold && <div className="mkt-faint">On hold by Twasta</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="mkt-faint">Stages and their shares are Twasta’s standard payment terms.</span>
    </section>
  )
}

function CurrentStagePanel({ ctx, stage }: { ctx: OrderCtx; stage: OrderStage }) {
  const { order } = ctx
  const client = order.role === 'client'
  const last = isLast(order, stage)
  const builderFirst = firstName(order.builder.person, order.builder.name)

  if (stage.status === 'disputed') {
    return (
      <div className="mkt-banner bad" role="status">
        <div>
          <strong>“{stage.name}” is disputed — its money is frozen</strong>
          {stage.dispute_note && <>“{stage.dispute_note}” </>}Twasta looks at both sides and decides; you’ll both be told.
        </div>
      </div>
    )
  }

  if (!client) return <BuilderStagePanel ctx={ctx} stage={stage} last={last} />

  // ── client ──
  if (stage.status === 'payment_due') return <PaymentDuePanel ctx={ctx} stage={stage} />
  if (stage.status === 'in_review') {
    if (!order.approver) {
      return (
        <div className="mkt-col" style={{ gap: 12 }}>
          <ApproverNote />
          <ReviewPanel ctx={ctx} stage={stage} last={last} />
        </div>
      )
    }
    return last ? <FinalSignOff {...ctx} stage={stage} /> : <ReviewPanel ctx={ctx} stage={stage} last={last} />
  }
  if (stage.status === 'changes_requested') {
    return (
      <div className="mkt-banner warn" role="status">
        <div>
          <strong>You asked for changes to “{stage.name}”</strong>
          {stage.changes_note && <span style={{ whiteSpace: 'pre-line' }}>“{stage.changes_note}”<br /></span>}
          The money stays with Twasta. {builderFirst} resubmits it when it’s fixed, and your review starts again.
        </div>
      </div>
    )
  }
  if (stage.status === 'approved') {
    return (
      <div className="mkt-banner ok" role="status">
        <div><strong>“{stage.name}” is approved</strong>Twasta is paying {builderFirst} for it.</div>
      </div>
    )
  }
  // pending
  return (
    <section className="mkt-panel mkt-col" aria-label="Stage being built">
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <h2>{builderFirst} is working on “{stage.name}”</h2>
        <StagePill stage={stage} role={order.role} />
      </div>
      <span className="mkt-muted">
        {stage.target_date ? `Expected around ${fmtDate(stage.target_date)}. ` : ''}
        {order.terms.collect_on === 'submit'
          ? `${money(stage.client.total_minor, order.currency)} is collected from your organization wallet when ${builderFirst} submits it.`
          : `${money(stage.client.total_minor, order.currency)} is collected from your organization wallet when you approve it.`}
      </span>
      <DeliverableList text={stage.deliverables} />
    </section>
  )
}

function ApproverNote() {
  return (
    <div className="mkt-banner info" role="note">
      <div>
        <strong>Only your organization’s administrators or project owners can approve spending</strong>
        You can look at the work and ask for changes; an administrator or project owner approves and pays.
      </div>
    </div>
  )
}

function DeliverableList({ text }: { text: string }) {
  const items = splitDeliverables(text)
  if (!items.length) return null
  return (
    <div className="mkt-col" style={{ gap: 6 }}>
      <strong style={{ fontSize: 14 }}>What this stage delivers</strong>
      <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.map((d, i) => <li key={i}>{d}</li>)}
      </ul>
    </div>
  )
}

// ── builder ─────────────────────────────────────────────────────────────────

function BuilderStagePanel({ ctx, stage, last }: { ctx: OrderCtx; stage: OrderStage; last: boolean }) {
  const { order, act, busy } = ctx
  const [note, setNote] = useState('')
  const clientName = order.client.name || 'the client'
  if (stage.status === 'in_review') {
    return (
      <div className="mkt-banner info" role="status">
        <div>
          <strong>“{stage.name}” is waiting for {clientName}’s approval</strong>
          Submitted {fmtDateTime(stage.submitted_at)}.
          {stage.review_due_at ? ` ${order.terms.on_no_response === 'auto_approve' ? 'If they don’t respond, it is approved automatically on' : 'If they don’t respond, Twasta Support steps in on'} ${fmtDate(stage.review_due_at)}.` : ''}
          {' '}{money(stage.amount_minor, order.currency)} is held by Twasta and paid to you when they approve{last ? ' and sign off' : ''}.
        </div>
      </div>
    )
  }
  if (stage.status === 'payment_due') {
    return (
      <div className="mkt-banner warn" role="status">
        <div><strong>Waiting for {clientName} to pay “{stage.name}”</strong>Their organization wallet didn’t cover it. Their review starts as soon as it is paid.</div>
      </div>
    )
  }
  if (stage.status === 'approved') {
    return (
      <div className="mkt-banner ok" role="status">
        <div><strong>“{stage.name}” is approved</strong>Twasta pays your share into your earnings once the checks are done.</div>
      </div>
    )
  }
  if (!order.can.submit) return null
  return (
    <section className="mkt-panel mkt-panel-accent mkt-col" aria-label="Submit the stage">
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <h2>{last ? 'Submit the finished app for sign-off' : `Submit “${stage.name}” for approval`}</h2>
        <StagePill stage={stage} role={order.role} />
      </div>
      {stage.status === 'changes_requested' && stage.changes_note && (
        <div className="mkt-banner warn"><div><strong>{clientName} asked for changes</strong><span style={{ whiteSpace: 'pre-line' }}>{stage.changes_note}</span></div></div>
      )}
      <DeliverableList text={stage.deliverables} />
      <NoteField id="submit-note" label={`Note for ${clientName} (optional)`} value={note} onChange={setNote}
        placeholder="What to try first, what changed since last time, anything to know." />
      <span className="mkt-muted" style={{ fontSize: 14 }}>
        {order.terms.collect_on === 'submit'
          ? `When you submit, Twasta collects ${money(stage.amount_minor, order.currency)} from ${clientName} and holds it until they approve.`
          : `${money(stage.amount_minor, order.currency)} is collected from ${clientName} when they approve.`}
        {stage.target_date ? ` Target date: ${fmtDate(stage.target_date)}.` : ''}
      </span>
      <div className="mkt-row">
        <button type="button" className="btn btn-primary" disabled={busy}
          onClick={async () => { if (await act(() => submitStage(order.id, stage.idx, note.trim()), 'Submitted for approval')) setNote('') }}>
          {busy ? 'Submitting…' : 'Submit for approval'}
        </button>
      </div>
    </section>
  )
}

// ── client: payment due ────────────────────────────────────────────────────

function PaymentDuePanel({ ctx, stage }: { ctx: OrderCtx; stage: OrderStage }) {
  const { order, act, busy } = ctx
  return (
    <section className="mkt-panel mkt-panel-danger mkt-col" aria-label="Payment needed">
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <h2>“{stage.name}” is submitted — {money(stage.client.total_minor, order.currency)} to pay</h2>
        <StagePill stage={stage} role={order.role} />
      </div>
      <span className="mkt-muted">
        {firstName(order.builder.person, order.builder.name)} submitted it{stage.submitted_at ? ` on ${fmtDate(stage.submitted_at)}` : ''}.
        Your organization wallet didn’t have enough, so it couldn’t be collected automatically. Your review starts as soon as it is paid.
      </span>
      <div className="mkt-col" style={{ gap: 4, fontSize: 14 }}>
        <span className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Stage share ({stage.pct}%)</span><span>{money(stage.client.base_minor, order.currency)}</span></span>
        <span className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Service fee ({stage.client.service_fee_percent}%)</span><span>{money(stage.client.service_fee_minor, order.currency)}</span></span>
        <span className="mkt-row" style={{ justifyContent: 'space-between' }}><strong>To pay</strong><strong>{money(stage.client.total_minor, order.currency)}</strong></span>
      </div>
      {order.can.pay ? (
        <div className="mkt-row">
          <button type="button" className="btn btn-primary" disabled={busy}
            onClick={() => act(() => payStage(order.id, stage.idx), 'Paid — your review has started')}>
            Pay {money(stage.client.total_minor, order.currency)} from the wallet
          </button>
        </div>
      ) : !order.approver ? <ApproverNote /> : null}
      <span className="mkt-faint">Paid from your organization wallet. GST was charged when the wallet was topped up.</span>
    </section>
  )
}

// ── client: review a stage ─────────────────────────────────────────────────

type Decision = 'ok' | 'change' | null
interface StoredDecision { item?: string; decision?: string }

function initialDecisions(stage: OrderStage, items: string[]): Decision[] {
  const prev = Array.isArray(stage.review) ? (stage.review as StoredDecision[]) : []
  return items.map((it) => {
    const hit = prev.find((p) => p && p.item === it)
    return hit?.decision === 'ok' || hit?.decision === 'change' ? (hit.decision as Decision) : null
  })
}

export function ReviewPanel({ ctx, stage, last }: { ctx: OrderCtx; stage: OrderStage; last: boolean }) {
  const { order, act, busy } = ctx
  const items = useMemo(() => splitDeliverables(stage.deliverables), [stage.deliverables])
  const [dec, setDec] = useState<Decision[]>(() => initialDecisions(stage, items))
  useEffect(() => setDec(initialDecisions(stage, items)), [stage.idx, stage.submitted_at, items]) // eslint-disable-line react-hooks/exhaustive-deps
  const [tried, setTried] = useState(false)
  const [asking, setAsking] = useState(false)
  const [note, setNote] = useState('')
  const builderFirst = firstName(order.builder.person, order.builder.name)

  const changed = items.filter((_, i) => dec[i] === 'change')
  const accepted = items.filter((_, i) => dec[i] === 'ok').length
  const decisions = items.map((item, i) => ({ item, decision: dec[i] || 'unset' }))
  const set = (i: number, d: Decision) => setDec((cur) => cur.map((x, j) => (j === i ? (x === d ? null : d) : x)))

  const advance = order.stages[0]
  const withAdvance = order.terms.advance_release === 'first_approval' && advance && advance.idx !== stage.idx
    && advance.status !== 'released' && !order.stages.slice(1).some((s) => s.status === 'approved' || s.status === 'released')
  const changeNote = [note.trim(), changed.length ? `Needs a change:\n${changed.map((c) => `• ${c}`).join('\n')}` : '']
    .filter(Boolean).join('\n\n')

  const sendChanges = async () => {
    if (!changeNote) return
    if (await act(() => requestStageChanges(order.id, stage.idx, changeNote, decisions), `Change request sent to ${builderFirst}`)) {
      setAsking(false); setNote('')
    }
  }

  return (
    <section className="mkt-panel mkt-panel-accent mkt-col" aria-label="Review the stage" style={{ gap: 16 }}>
      <div className="mkt-col" style={{ gap: 4 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <h2>{last ? 'The finished app is ready for sign-off' : `“${stage.name}” is ready for your approval`}</h2>
          <StagePill stage={stage} role={order.role} />
        </div>
        <span className="mkt-muted">
          {builderFirst} submitted it {fmtDateTime(stage.submitted_at)} · {stage.pct}% of the order.
          {order.preview_path ? ' Try it in the working copy, then approve or ask for changes.' : ''}
        </span>
      </div>

      {stage.submit_note && (
        <div className="mkt-banner plain"><div><strong>Note from {builderFirst}</strong><span style={{ whiteSpace: 'pre-line' }}>“{stage.submit_note}”</span></div></div>
      )}

      {items.length > 0 && (
        <div className="mkt-col" style={{ gap: 8 }}>
          <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
            <strong>Check each deliverable</strong>
            {order.preview_path && <a href={order.preview_path} target="_blank" rel="noopener" style={{ fontSize: 14 }}>Open the working copy</a>}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((it, i) => (
              <li key={i} className="mkt-row" style={{
                justifyContent: 'space-between', padding: '10px 12px', borderRadius: 12,
                border: `1px solid ${dec[i] === 'change' ? 'var(--color-warning)' : 'var(--color-border)'}`,
                background: dec[i] === 'change' ? 'var(--color-warning-weak)' : 'var(--color-bg)',
              }}>
                <span style={{ flex: '1 1 240px' }}>{it}</span>
                <span className="mkt-row" style={{ gap: 6 }} role="group" aria-label={`Decision for ${it}`}>
                  <button type="button" className={`btn btn-sm ${dec[i] === 'ok' ? 'btn-primary' : 'btn-secondary'}`} aria-pressed={dec[i] === 'ok'} onClick={() => set(i, 'ok')}>✓ Accept</button>
                  <button type="button" className={`btn btn-sm ${dec[i] === 'change' ? 'btn-primary' : 'btn-secondary'}`} aria-pressed={dec[i] === 'change'} onClick={() => set(i, 'change')}>Needs change</button>
                </span>
              </li>
            ))}
          </ul>
          <span className="mkt-faint" aria-live="polite">
            {accepted} of {items.length} accepted{changed.length ? ` · ${changed.length} need${changed.length === 1 ? 's' : ''} a change` : ''}
          </span>
        </div>
      )}

      <div className="mkt-col" style={{ gap: 6, borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Stage value ({stage.pct}%)</span><strong>{money(stage.amount_minor, order.currency)}</strong></div>
        {stage.collected_at && <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Held by Twasta since</span><span>{fmtDate(stage.collected_at)}</span></div>}
        {stage.review_due_at && (
          <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
            <span className="mkt-muted">{order.terms.on_no_response === 'auto_approve' ? 'Approves itself on' : 'Twasta Support steps in on'}</span>
            <span>{fmtDate(stage.review_due_at)}</span>
          </div>
        )}
      </div>

      {!last && order.can.approve && (
        <div className="mkt-col" style={{ gap: 10 }}>
          <label className="mkt-row" style={{ gap: 8, cursor: 'pointer', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
            <input type="checkbox" checked={tried} onChange={(e) => setTried(e.target.checked)} style={{ marginTop: 4 }} />
            <span>I tried it in the working copy and it does what the quote says.</span>
          </label>
          {changed.length > 0 && <span className="mkt-faint">Some items are marked “Needs change” — ask for changes, or mark them accepted to approve.</span>}
          <div className="mkt-row">
            <button type="button" className="btn btn-primary" disabled={busy || !tried || changed.length > 0}
              onClick={() => act(() => approveStage(order.id, stage.idx, decisions), `Approved — Twasta is paying ${builderFirst}`)}>
              Approve — Twasta pays {builderFirst} {money(stage.amount_minor + (withAdvance ? advance.amount_minor : 0), order.currency)}
            </button>
            {order.can.changes && !asking && (
              <button type="button" className="btn btn-secondary" onClick={() => setAsking(true)}>
                Ask for changes{changed.length ? ` (${changed.length})` : ''}
              </button>
            )}
          </div>
          {withAdvance && <span className="mkt-faint">Includes the advance ({money(advance.amount_minor, order.currency)}), which is paid with the first approval.</span>}
        </div>
      )}

      {(last || !order.can.approve) && order.can.changes && !asking && (
        <div className="mkt-row">
          <button type="button" className="btn btn-secondary" onClick={() => setAsking(true)}>
            Ask for changes{changed.length ? ` (${changed.length})` : ''}
          </button>
        </div>
      )}

      {asking && (
        <div className="mkt-col" style={{ gap: 10, border: '1px solid var(--color-border)', borderRadius: 12, padding: 14 }}>
          <NoteField id="changes-note" label={`What should ${builderFirst} change?`} value={note} onChange={setNote} rows={4}
            hint={changed.length ? `The ${changed.length} item(s) marked “Needs change” are added to your note.` : undefined} />
          <span className="mkt-muted" style={{ fontSize: 14 }}>
            {money(stage.amount_minor, order.currency)} stays with Twasta. Your review starts again when {builderFirst} resubmits.
          </span>
          <div className="mkt-row">
            <button type="button" className="btn btn-primary" disabled={busy || !changeNote} onClick={sendChanges}>Send change request</button>
            <button type="button" className="btn btn-ghost" onClick={() => setAsking(false)}>Not now</button>
          </div>
        </div>
      )}
    </section>
  )
}

// ── dispute + cancel ────────────────────────────────────────────────────────

function DisputePanel({ ctx, stage }: { ctx: OrderCtx; stage: OrderStage }) {
  const { order, act, busy } = ctx
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  if (!open) {
    return (
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <span className="mkt-muted" style={{ fontSize: 14 }}>A problem with “{stage.name}” you can’t agree on?</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>Open a dispute</button>
      </div>
    )
  }
  return (
    <section className="mkt-panel mkt-panel-danger mkt-col" aria-label="Open a dispute">
      <h2>Open a dispute on “{stage.name}”</h2>
      <span className="mkt-muted" style={{ fontSize: 14 }}>
        The stage’s money is frozen until Twasta looks at both sides and decides. Try a message first — most things are settled that way.
      </span>
      <NoteField id="dispute-note" label="What is the disagreement?" value={note} onChange={setNote} rows={4} />
      <div className="mkt-row">
        <button type="button" className="btn btn-danger" disabled={busy || !note.trim()}
          onClick={async () => { if (await act(() => disputeStage(order.id, stage.idx, note.trim()), 'Dispute opened — Twasta will look at it')) { setOpen(false); setNote('') } }}>
          Open the dispute
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Not now</button>
      </div>
    </section>
  )
}

function CancelPanel({ ctx }: { ctx: OrderCtx }) {
  const { order, act, busy } = ctx
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const workStarted = order.stages.some((s, i) => i > 0 && s.submitted_at)
  if (!open) {
    return (
      <div className="mkt-row" style={{ justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
        <span className="mkt-muted" style={{ fontSize: 14 }}>No longer need this app built?</span>
        <button type="button" className="btn btn-danger btn-sm" onClick={() => setOpen(true)}>Cancel order</button>
      </div>
    )
  }
  return (
    <section className="mkt-panel mkt-panel-danger mkt-col" aria-label="Cancel the order">
      <h2>Cancel “{order.title}”?</h2>
      <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <li>Money Twasta holds for stages you haven’t approved goes back to your organization wallet{workStarted ? '' : ', with the service fee'}.</li>
        <li>Stages you already approved stay paid to the builder.</li>
        <li>The working copy stops being available to you.</li>
      </ul>
      <NoteField id="cancel-reason" label="Why are you cancelling?" value={reason} onChange={setReason} hint={`${order.builder.name} is told the reason.`} />
      <div className="mkt-row">
        <button type="button" className="btn btn-primary" onClick={() => setOpen(false)}>Keep the order</button>
        <button type="button" className="btn btn-danger" disabled={busy || !reason.trim()}
          onClick={async () => { if (await act(() => cancelOrder(order.id, reason.trim()), 'Order cancelled')) setOpen(false) }}>
          Cancel the order
        </button>
      </div>
    </section>
  )
}
